import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(20vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        position: relative;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header video {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;

        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .content .text {
        margin: 0 0 10px 0;
        gap: 8px;
        align-items: center;
        width: 0; 
        animation: typing 1s forwards;
        overflow: hidden;
        max-width: 1024px;

        text-align: center;
    }

    @keyframes typing {
        from { width: 0; }
        to { width: 100%; }
    }

    .content .text img {
        width: 20px;
        height: 20px;
        object-fit: contain;
    }

    .content .text:last-of-type {
        margin: 0;
    }

    .container {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .footer {
        border-radius: 10px 0 0 0;
        background-color:rgb(139, 71, 71);
        height: 150px;
        display: flex;
        flex-direction: column;
        max-width: 1024px;
        flex-grow: 1;
    }

    .footer .buttons {
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .player-info-container {
        display: grid;
        grid-template-columns: 390px 525px;
        justify-content: space-between;
        align-items: end;
        max-width: 1024px;
        flex-grow: 1;
    }

    .player-info-container .health {
        display: flex;
        align-items: center;
        justify-content: start;
        gap: 10px;
        margin: 20px 0 20px 10px;
    }

    .player-info-container .inventory {
        display: flex;
        align-items: center;
    }

    .health .health-container {
        width: 100%;
        border: 3px solid rgb(252, 252, 252);
        border-radius: 5px;
        display: flex;
        justify-content: start;
        align-items: center;
    }

    .health img {
        width: 36px;
        height: 36px;
        object-fit: contain;
    }


    .health-container #health-bar {
        background: linear-gradient(to right,rgb(105, 15, 15),rgb(255, 0, 0));
        height: 20px;
    }

    .inventory .inventory-container {
        padding: 0px 10px;
        width: 100%;
        height: 90px;
        background-color:rgb(139, 71, 71);
        border-radius: 5px 5px 0 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }       

    .item-container, .unavailable-item-container {
        background-color: rgb(0, 0, 0);
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 70px;
        height: 70px;
    }

    .inventory-container img, .unavailable-item-container img {
        object-fit: contain;
        width: 100%;
        height: 100%;
    }

    .footer .button {
        background-color:rgb(81, 55, 55);
        border: 2px solid rgb(255, 255, 255);
        border-radius: 5px; 
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: rgb(54, 42, 42);
    }

    .wood-minigame {
        width: 100%;
        text-align: center;
        
        .game-container {
            position: relative;
            width: 600px;
            height: 50px;
            border: 2px solid white;
            margin: 20px auto;
            overflow: hidden;
            border-radius: 100px;

            .moving-bar {
                position: absolute;
                width: 10px;
                height: 100%;
                background-color: #c22020;
            }

            .target-zone {
                position: absolute;
                width: 50px;
                height: 100%;
                background-color: #00ff00;
            }
        }
    }

    .healthenemy .healthenemy-container {
        width: 100%;
        border: 3px solid rgb(252, 252, 252);
        border-radius: 5px;
        display: flex;
        justify-content: end;
        align-items: center;
    }

    .healthenemy img {
        width: 36px;
        height: 36px;
        object-fit: contain;
    }


    .healthenemy-container #healthenemy-bar {
        background: linear-gradient(to right,rgb(105, 15, 15),rgb(255, 0, 0));
        height: 20px;
    }

    .healthenemy {
        display: flex;
        align-items: center;
        justify-content: start;
        gap: 10px;
        margin: 20px 0 20px 10px;
    }

    video::-webkit-media-controls-play-button,
    video::-webkit-media-controls-fullscreen-button,
    video::-webkit-media-controls-timeline,
    video::-webkit-media-controls-current-time-display,
    video::-webkit-media-controls-time-remaining-display {
        display: none;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            `;

        const isPlayingForest: boolean = this._currentGameState?.roomName === "The Forest - Playing";
        const isPlayingFish: boolean = this._currentGameState?.roomName === "The Beachside - Fishing";
        const isPlayingBattleRoom: boolean = this._currentGameState?.roomName === "LesserBattleRoom";
        const encounteredEnemy: boolean = this._currentGameState?.encounteredEnemy as boolean;
        const isbattlingboss: boolean = this._currentGameState?.roomAlias === "BossBattleRoom";
        const inStartup: boolean = this._currentGameState?.roomAlias === "startup";
        const introPlayed: boolean = localStorage.getItem("videoPlayed") === "true";

        if (isPlayingForest) {
            elements.push(this.renderWoodMinigame(), this.renderPlayerInfo(), this.renderFooter());
        }
        else if (isPlayingFish) {
            elements.push(this.renderContent(), this.renderFishMinigame(), this.renderPlayerInfo(), this.renderFooter());
        }
        else if (isPlayingBattleRoom || encounteredEnemy) {
            elements.push(this.renderContent(), this.Nativepersoninfo(isbattlingboss), this.renderPlayerInfo(), this.renderFooter());
        }
        else if (isbattlingboss) {
            elements.push(this.renderContent(), this.Nativepersoninfo(isbattlingboss), this.renderPlayerInfo(), this.renderFooter());
        }
        else if (inStartup) {
            elements.push(this.renderContent());

            if (introPlayed) {
                elements.push(this.renderFooter());
            }
        }
        else {
            elements.push(this.renderContent());
            elements.push(this.renderPlayerInfo());
            elements.push(this.renderFooter());
        }
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        elements.push(html`<overlay-canvas></overlay-canvas>`);

        this.shadowRoot.append(...elements);
        if (isPlayingForest) {
            this.startMinigame();
        }
        else if (isPlayingFish) {
            this.waitForFishGame();
        }
        else if (inStartup) {
            this.waitForVideo();
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        // Get room images from game state
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        // Check if gamestate contains images
        if (roomImages && roomImages.length > 0) {
            // Create a header element with the image
            let headerElement: string = `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
            // If the supplied string includes ".mp4", the header element is changed to a video
            if (roomImages.some(url => url.includes("mp4"))) {
                const introPlayed: boolean = localStorage.getItem("videoPlayed") === "true";

                if (introPlayed) {
                    headerElement = `
                    <div class="header">
                        <img src="/assets/img/rooms/introplayed.png" />
                    </div>`;
                }
                else {
                    headerElement = `
                        <div class="header">
                            ${roomImages.map(url => `<video src="/assets/img/rooms/${url}" autoplay controls muted /></video>`).join("")}
                        </div>
                    `;
                }
            }
            // If a mission is active, the canvas will be shown.
            if (this._currentGameState?.doingRajanMission || this._currentGameState?.doingBoatMission) {
                headerElement = `
                    <div class="header">
                        ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                        <mission-canvas></mission-canvas>
                    </div>
                `;
            }

            // The header element is returned to the user
            return headerElement;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): HTMLElement {
        return html`
            <div class="content">
                ${this._currentGameState?.text.map(text => `<div class="text">${text}</div>`).join("") || ""}
            </div>
        `;
    }

    private renderPlayerInfo(): HTMLElement {
        if (this._currentGameState?.roomName === "Example Game") {
            return html``;
        }
        const healthBarHTMLElement: HTMLElement = this.updateHealthBar(this._currentGameState!.health);
        const inventoryItemsHTML: string = this.renderInventoryItems();
        return html`
            <div class="container">
                <div class="player-info-container">
                    <div class="health">
                    <img src="/assets/img/game-icons/heart-icon.png" alt="Heart icon">
                        <div class="health-container">
                            ${healthBarHTMLElement.outerHTML}
                        </div>
                    </div>
                    <div class="inventory">
                        <div class="inventory-container">
                                ${inventoryItemsHTML}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        // Filters the game objects based on the selected action button
        let currentGameObjects: GameObjectReference[] = [];
        if (this._selectedActionButton?.gameObjectAliases !== undefined) {
            if (this._selectedActionButton.gameObjectAliases.length !== 0) {
                for (const objectAlias of this._selectedActionButton.gameObjectAliases) {
                    if (this._currentGameState?.objects.some(alias => alias.alias === objectAlias)) {
                        currentGameObjects.push(this._currentGameState.objects.find(alias => alias.alias === objectAlias) as GameObjectReference);
                    }
                }
            }
            else {
                currentGameObjects = [];
            }
        }
        else {
            currentGameObjects = this._currentGameState?.objects || [];
        }
        return html`
            <div class="container">
                <div class="footer">
                    <div class="buttons">
                        <div>
                            ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                        </div>
                        <div>
                        ${this._selectedActionButton
                            ? currentGameObjects.map(button => this.renderGameObjectButton(button))
                            : ""
                            } 
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    private renderInventoryItems(): string {
        const inventoryItems: string[] | undefined = this._currentGameState?.inventory;
        const hasSuitcase: boolean = this._currentGameState?.hasSuitcase as boolean || false;

        let inventoryItemsHTML: string = "";
        for (let i: number = 0; i < 6; i++) {
            if (inventoryItems === undefined) {
                inventoryItemsHTML += undefinedInventoryItems(i, hasSuitcase).outerHTML;
            }
            else {
                if (i < inventoryItems.length) {
                    inventoryItemsHTML += renderInventoryItem(i).outerHTML;
                }
                else {
                    inventoryItemsHTML += undefinedInventoryItems(i, hasSuitcase).outerHTML;
                }
            }
        }

        function renderInventoryItem(index: number): HTMLElement {
            return html`
                <div class="item-container">
                    <img src="/assets/img/item-icons/${inventoryItems![index]}.png" alt="${inventoryItems![index]}">
                </div>
            `;
        }

        function undefinedInventoryItems(index: number, hasSuitcase: boolean): HTMLElement {
            if (index > 3 && !hasSuitcase) {
                return renderUnavailableInventorySlots();
            }
            return html`
                <div class="item-container">
                </div>
            `;
        }

        function renderUnavailableInventorySlots(): HTMLElement {
            return html`
                <div class="unavailable-item-container">
                            <img src="/assets/img/item-icons/locked.png" alt="Locked icon">
                        </div>
            `;
        }

        return inventoryItemsHTML;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    private updateHealthBar(playerHealth: number): HTMLElement {
        const healthBar: HTMLDivElement = document.createElement("div");
        healthBar.id = "health-bar";
        const healthPercentage: number = (playerHealth / 10) * 100;
        healthBar.style.width = `${healthPercentage}%`;
        return healthBar;
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }

    /**
     * Render the wood minigame
     * @returns HTML element of the wood minigame
     */
    private renderWoodMinigame(): HTMLElement {
        return html`
        <div class="wood-minigame">
            <div class="game-container">
                <div class="target-zone"></div>
                <div class="moving-bar"></div>
            </div>
            <div class="game-info">
                <p>Press space to chop the tree!</p>
                <p>Chops: <span class="chops">0</span>/3</p>
            </div>
        </div>
        `;
    }

    /**
     * Wait for the video to end and then render the next state
     * @returns void
     */
    private waitForVideo(): void {
        const video: HTMLVideoElement | null = this.shadowRoot!.querySelector("video");

        if (video === null) return;
        video.addEventListener("ended", () => {
            localStorage.setItem("videoPlayed", "true");

            this.render();
        });
    }

    // Variables for Forest minigame
    private movingBar: HTMLElement | null = null;
    private targetZone: HTMLElement | null = null;
    private chopsSpan: HTMLElement | null = null;
    private minigameStarted: boolean = false;
    private listenerRegistered: boolean = false;

    private direction: number = 1;
    private speed: number = Math.random() * 2 + 1;
    private position: number = 0;

    private chops: number = 0;

    /**
     * @description Set a random target for the minigame
     */
    private setRandomTarget(): void {
        const targetPos: number = Math.random() * 550;

        // Check if target zone exists and set it to the target position
        if (this.targetZone) {
            this.targetZone.style.left = targetPos.toString() + "px";
        }
    }

    /**
     * @description Move the bar in the minigame.
     */
    private moveBar(): void {
        // Check if the minigame has started
        if (!this.minigameStarted) {
            return;
        }
        else {
            // Change the position of the bar depending on the dynamic speed
            this.position += this.direction * this.speed;
            // Switch the direction of the bar if it reaches the end of the game container
            if (this.position >= 590 || this.position <= 0) {
                this.direction *= -1;
            }

            // Move the bar element
            if (this.movingBar) {
                this.movingBar.style.left = this.position.toString() + "px";
            }

            // Request the browser to execute this function before the next repaint
            requestAnimationFrame(() => this.moveBar());
        }
    }

    /**
     * @description Chop the tree in the minigame
     */
    private async chopTree(): Promise<void> {
        // Check positions
        const barPos: number = this.movingBar ? this.movingBar.offsetLeft : 0;
        const targetPos: number = this.targetZone ? this.targetZone.offsetLeft : 0;

        // Check if the bar is in the target zone
        if (barPos >= targetPos && barPos <= targetPos + 50) {
            this.chops++;

            // Check if the player has chopped the tree 3 times
            if (this.chops === 3) {
                this.chops = 0;
                this.minigameStarted = false;

                // Execute the give-wood action and update the game state
                const state: GameState | undefined = await this._gameRouteService.executeAction("give-wood", []);

                if (state === undefined) {
                    return;
                }

                this.updateGameState(state);
            }
        }

        // Missed the target
        else {
            if (this.chops !== 0) {
                this.chops--;
            }
        }

        // Update the chops span
        if (this.chopsSpan) {
            this.chopsSpan.innerText = this.chops.toString();
        }

        // Set a new random target
        this.setRandomTarget();
        this.speed = Math.random() * 2 + 1;
    }

    /**
     * @description Start the minigame
     */
    public startMinigame(): void {
        // Get the elements
        this.movingBar = this.shadowRoot!.querySelector("div.wood-minigame > div > div.moving-bar") as HTMLElement;
        this.targetZone = this.shadowRoot!.querySelector("div.wood-minigame > div > div.target-zone") as HTMLElement;
        this.chopsSpan = this.shadowRoot!.querySelector("div.wood-minigame > div.game-info > p > span") as HTMLElement;

        this.minigameStarted = true;
        this.setRandomTarget();
        this.moveBar();

        // Register EventListener
        if (!this.listenerRegistered) {
            this.listenerRegistered = true;

            document.addEventListener("keydown", async event => {
                if (event.key === " ") {
                    if (this.minigameStarted && this._currentGameState?.roomName === "The Forest - Playing") {
                        await this.chopTree();
                    }
                }
            });
        }
    }

    private Nativepersoninfo(isbattlingBoss: boolean): HTMLElement {
        // Get the health based on whether it's for the boss or the enemy
        const health: number = isbattlingBoss ? this._currentGameState!.bosshealth : this._currentGameState!.enemyhealth;

        // Always use the native face image for both cases
        const imageSrc: string = "/assets/img/game-icons/native-face-icon.png"; // Fixed to always use the native face icon

        const healthBarId: string = "healthenemy-bar"; // The ID for the health bar remains the same
        const healthBarHTMLElement: HTMLElement = this.updateenemyHealthBar(health, healthBarId); // Update health bar

        // Return the HTML structure
        return html`
            <div class="container">
                <div class="player-info-container">
                    <div class="healthenemy">
                        <img src="${imageSrc}" alt="Native Face">
                        <div class="healthenemy-container">
                            ${healthBarHTMLElement.outerHTML}
                        </div>
                    </div>    
                </div>
            </div>
        `;
    }

    private updateenemyHealthBar(health: number, healthBarId: string): HTMLElement {
        const healthBar: HTMLDivElement = document.createElement("div");
        healthBar.id = healthBarId;

        // Adjust the percentage calculation based on whether it's a boss or enemy
        const healthPercentage: number = (health / 10) * (healthBarId === "healthenemy-bar" ? 100 : 50);
        healthBar.style.width = `${healthPercentage}%`;

        return healthBar;
    }

    /**
     * Render the fish minigame
     * @returns HTML element of the fish minigame
     */
    private renderFishMinigame(): HTMLElement {
        return html`<fish-minigame></fish-minigame>`;
    }

    /**
     * Wait for the fish minigame to finish and then update the game state
     */
    private waitForFishGame(): void {
        const fishMinigame: HTMLElement = ((((((document.querySelector("body > game-root") as HTMLElement)
            .shadowRoot) as ShadowRoot).querySelector("game-canvas") as HTMLElement).shadowRoot) as ShadowRoot)
            .querySelector("fish-minigame") as HTMLElement;

        fishMinigame.addEventListener("succeeded-fishing", async () => {
            const state: GameState | undefined = await this._gameRouteService.executeAction("give-fish", []);

            if (state === undefined) {
                return;
            }

            this.updateGameState(state);
        });
    }
}
